/**
 * Application wide configuration.
 */
function getEnvVar(key: string, defaultValue = ''): string {
  const value = process.env[key] || import.meta.env[key] || undefined;

  return value || defaultValue;
}

const parseNumberList = (value: string | undefined): number[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isFinite(id));
};

const endpoints = {
  users: 'users',
  userProfile: 'users/:id',
  userProjectAllocations: '/allocations',
  userPermissions: 'users/:id/permissions',
  userActivate: 'users/:id/activate',
  userStakeholders: 'allocations/user-stakeholders',
  roles: 'roles',
  rolesPermissions: 'roles/permissions',
  grantRolesPermissions: 'roles/:id/permissions',
  revokeRolesPermissions: 'roles/:id/permissions/:permissionId',
  permissions: 'permissions',
  information: 'me/info',
  userRoles: 'users/:id/roles',
  resource: 'users/resource',
  peopleFinder: 'people-finder',
  peopleFinderExport: 'people-finder/export',
  resourceExport: 'users/resource/export',
  userSkills: 'users/:id/skills',
  userTeam: 'users/:id/team',
  userImageUpload: 'users/:id/upload',
  updateUserAndNotifyLeaveIssuer: 'users/:id/leave-issuer',
  leaveIssuerChangeRequest: 'users/:userId/leave-issuer-history/change-request',
  updateUserAndNotifyTeamManager: 'users/:id/team-manager',
  updateUserEngagementStatus: 'users/:id/engagement-status',
  updateUserDesignation: 'users/:id/designation',
  userLeaveIssuerHistory: 'users/:id/leave-issuer-history',
  userTeamManagerHistory: 'users/:id/team-manager-history',
  leaveIssuerChangeRequestsList: 'leave-issuers/change-requests',
  leaveIssuerChangeRequestsListExport: 'leave-issuers/change-requests/export',
  bulkUpdateLeaveIssuerChangeRequest: 'leave-issuers/change-requests/status',
  updateLeaveIssuerChangeRequest: 'users/:userId/leave-issuer-history/change-request/status',
  departments: 'departments',
  designations: 'designations',
  designation: 'designations/:id',
  skill: 'skills/:id',
  skills: 'skills',
  skillsV2: 'skills/v2',
  skillV2: 'skills/v2/:id',
  skillV2Categories: 'skills/v2/categories',
  skillV2Category: 'skills/v2/categories/:id',
  userSkillsV2: 'users/:userId/skills/v2',
  userSkillsV2Status: 'users/:userId/skills/v2/:skillId/status',
  skillsV2globalratings: 'skills/v2/global-ratings',
  skillCategory: 'skill-categories/:id',
  skillCategories: 'skill-categories',
  areas: 'designation-areas',
  reminders: 'reminders',
  reminder: 'reminders/:id',
  remindersList: 'reminders/list',
  engagementStatus: 'engagement-status',
  leaveBalance: '/leave-balance',
  leaveApply: '/leaves/apply',
  leaveCalculate: '/leaves/calculate',
  leaveCredits: '/leave-credits',
  wellBeingLeaves: '/leave-credits/well-being',
  quickLinks: {
    quickLinks: 'quick-links',
    quickLinksCategories: '/quick-links/categories',
    quickLinksCategory: '/quick-links/categories/:id',
    quickLinksCategoryLinks: '/quick-links/categories/link',
    quickLinksCategoryLink: '/quick-links/categories/link/:id',
    weights: '/quick-links/weights',
  },
  events: 'events',
  event: 'events/:id',
  notices: 'notices',
  noticesList: 'notices/list',
  notice: 'notices/:id',
  noticeBoard: 'notices/noticeboard',
  notes: 'notes',
  note: 'notes/:id',
  occasion: 'occasions',
  holidays: 'holidays',
  holiday: 'holidays/:id',
  myTeam: 'my-team',
  teamSummary: 'users/team-summary',
  empAssets: 'assets/:id',
  auth: {
    login: '/login',
    token: '/token',
    logout: '/logout',
  },
  attendance: {
    app: {
      worklogDetail: '/worklogs/:id',
      leaveDetail: '/leaves/:id',
      applyLeave: '/leaves/apply',
      selfWorklog: '/self/worklog',
      teamWorklog: '/team/worklog',
      selfMissing: '/self/missing',
      teamLeave: '/team/leave',
    },
    api: {
      worklogs: '/worklogs',
      calendar: '/calendar',
      summary: '/attendance/summary',
      leaves: '/leaves',
      attendance: '/attendance',
      medianClockInTime: '/wifi-logs/median-time/:userId',
      workStatusSummary: '/attendance/work-status-summary',
      unfreezeRequests: '/worklog-unfreeze-requests/:userId',
      roster: '/roster',
    },
  },
  jump: {
    app: {
      feedbackDetail: '/feedbacks/:id',
    },
    userFeedbacks: '/feedbacks/team/:userId',
    goalFeedbacks: '/goals/:goalId/feedbacks',
    userGoals: '/goals/employees/:userId',
    userGoalsSummary: '/goals/employees/:userId/summary',
  },
  pulse: {
    userFeed: '/:id/feed',
    dashboard: '/dashboard',
  },
  lms: {
    userProfile: '/leapfroggers/:id',
    userProfileList: '/leapfroggers/',
    dashboard: '/homepage',
  },
  fiscalYears: '/fiscal-years',
  teams: {
    allocations: '/allocations',
    industries: '/industries',
    appraiserChangeRequest: '/allocations/:id/appraiser-change-request',
    bookingDetails: '/candidates/:userId/booking-details',
    app: {
      project: '/projects/:id/detail',
      offboards: '/projects/:id/detail/offboard',
    },
  },
  userResignations: '/users/:userId/resignations',
  userResignation: '/users/:userId/resignations/:id',
} as const;

const vyaguta = {
  dashboard: '/',
  people: '/leapfroggers',
  attendance: '/attendance',
  teams: '/teams',
  timesheet: '/timesheet',
  honor: '/honor',
  jump: '/jump',
  okr: '/okr',
  pulse: '/pulse',
} as const;

type AppConfig = {
  env: string | undefined;
  appName: string;
  appVersion: string | undefined;
  appEnv: string | undefined;
  basename: string | undefined;
  baseURI: string | undefined;
  coreBaseURI: string | undefined;
  jumpBaseURI: string | undefined;
  attendanceBaseURI: string | undefined;
  authURI: string | undefined;
  authClientId: string | undefined;
  loginURL: string | undefined;
  sentryDSN: string | undefined;
  vyagutaURI: string | undefined;
  newSkillRequestFormURI: string | undefined;
  empImageURI: string;
  projectLogoURI: string;
  mixPanelToken: string | undefined;
  mixPanelProxyDomain: string | undefined;
  fbAppId: string | undefined;
  fbAppVersion: string;
  googleOAuthScope: string;
  googleOAuthConsent: string;
  googleOAuthClientId: string | undefined;
  askUsAnythingForm: string;
  rolesInfoUrl: string | undefined;
  wellBeingLeavePolicy: string;
  teamsBaseURI: string | undefined;
  teamsAppURL: string | undefined;
  isMaintenanceMode: boolean;
  relatedHelpDocumentsUrl: string | undefined;
  peopleFeedbackUrl: string | undefined;
  bugReportUrl: string | undefined;
  events: {
    startDate: string;
    endDate: string;
    link: string;
    title: string;
  };
  freezeAttendance: {
    timeForLock: string;
  };
  pulseBaseUrl: string | undefined;
  pulseApiUrl: string | undefined;
  endpoints: typeof endpoints;
  vyaguta: typeof vyaguta;
  betaUserIdList: number[];
};

const config: AppConfig = {
  env: import.meta.env.MODE,
  appName: getEnvVar('REACT_APP_NAME', 'Vyaguta Core'),
  appVersion: getEnvVar('REACT_APP_VERSION'),
  appEnv: getEnvVar('REACT_APP_ENV'),
  basename: getEnvVar('REACT_APP_BASE_NAME'),
  baseURI: getEnvVar('REACT_APP_API_BASE_URI'),
  coreBaseURI: getEnvVar('REACT_APP_API_CORE_BASE_URI'),
  jumpBaseURI: getEnvVar('REACT_APP_API_JUMP_BASE_URI'),
  attendanceBaseURI: getEnvVar('REACT_APP_API_ATTENDANCE_BASE_URI'),
  authURI: getEnvVar('REACT_APP_AUTH_BASE_URI'),
  authClientId: getEnvVar('REACT_APP_AUTH_CLIENT_ID'),
  loginURL: getEnvVar('REACT_APP_LMS_URL'),
  sentryDSN: getEnvVar('REACT_APP_SENTRY_DSN'),
  vyagutaURI: getEnvVar('REACT_APP_VYAGUTA_URL'),
  newSkillRequestFormURI: getEnvVar('REACT_APP_NEW_SKILLS_REQUEST_FORM'),
  empImageURI: `${getEnvVar('REACT_APP_IMAGE_URI')}/employee/48/:empId.png`,
  projectLogoURI: `${getEnvVar('REACT_APP_IMAGE_URI')}/projects/logo/48/:projectId.png`,
  mixPanelToken: getEnvVar('REACT_APP_MIXPANEL_TOKEN'),
  mixPanelProxyDomain: getEnvVar('REACT_APP_MIXPANEL_PROXY_DOMAIN'),
  fbAppId: getEnvVar('REACT_APP_FB_APP_ID'),
  fbAppVersion: getEnvVar('REACT_APP_FB_APP_VERSION', 'v3.3'),
  googleOAuthScope: getEnvVar('REACT_APP_GOOGLE_OAUTH_SCOPE', 'profile email'),
  googleOAuthConsent: getEnvVar('REACT_APP_GOOGLE_OAUTH_CONSENT', 'select_account'),
  googleOAuthClientId: getEnvVar('REACT_APP_GOOGLE_OAUTH_CLIENT_ID'),
  askUsAnythingForm: getEnvVar(
    'REACT_APP_ASK_US_ANYTHING_LINK',
    'https://docs.google.com/forms/d/e/1FAIpQLSdfsJ0ZIDFg3d2_YAT4-Yp04kgs6aJ0sAVbF1s5qSjUkjQqVA/viewform'
  ),
  rolesInfoUrl: getEnvVar('REACT_APP_ROLES_CONFLUENCE_URL'),
  wellBeingLeavePolicy: getEnvVar(
    'REACT_APP_WELL_BEING_LEAVE_POLICY_URL',
    'https://lftechnology.atlassian.net/wiki/spaces/LEAP/pages/4321869940/Well-being+Leave+Policy+Nepal'
  ),
  teamsBaseURI: getEnvVar('REACT_APP_TEAMS_BASE_URI'),
  teamsAppURL: getEnvVar('REACT_APP_TEAMS_APP_URL'),
  isMaintenanceMode: Boolean(Number(getEnvVar('REACT_APP_MAINTENANCE_MODE', '0'))),
  relatedHelpDocumentsUrl: getEnvVar('REACT_APP_RELATED_HELP_DOCUMENTS_URL'),
  peopleFeedbackUrl: getEnvVar('REACT_APP_PEOPLE_FEEDBACK_URL'),
  bugReportUrl: getEnvVar('REACT_APP_BUG_REPORT_URL'),
  events: {
    startDate: getEnvVar('REACT_APP_EVENT_START_DATE', '2023-07-07 09:00:00'),
    endDate: getEnvVar('REACT_APP_EVENT_END_DATE', '2023-07-07 18:00:00'),
    link: getEnvVar(
      'REACT_APP_EVENT_VIDEO_LINK',
      'https://drive.google.com/file/d/1AQnvquenAsff-l9htGzaiLZMRnLVFhbI/preview'
    ),
    title: getEnvVar('REACT_APP_EVENT_TITLE', 'To Watch The Q3 All Hands Video: Click Here'),
  },
  freezeAttendance: {
    timeForLock: getEnvVar('REACT_APP_FREEZE_ATTENDENCE', '07:00 PM'),
  },
  pulseBaseUrl: getEnvVar('REACT_APP_PULSE_APP_URL'),
  pulseApiUrl: getEnvVar('REACT_APP_API_PULSE_BASE_URI'),
  endpoints,
  vyaguta,
  betaUserIdList: parseNumberList(getEnvVar('REACT_APP_REMOTE_WORK_BETA_USER_LIST')),
};

export type { AppConfig };
export default config;
